import React from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  PauseCircle,
  PlayCircle,
  CircleDot,
} from 'lucide-react';

export type StatusType =
  | 'active'
  | 'inactive'
  | 'pending'
  | 'completed'
  | 'cancelled'
  | 'in-progress'
  | 'paused'
  | 'overdue';

interface StatusBadgeProps {
  status: StatusType;
  showIcon?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const statusConfig: Record<
  StatusType,
  {
    label: string;
    icon: React.ElementType;
    className: string;
  }
> = {
  active: {
    label: 'Active',
    icon: CheckCircle2,
    className: 'bg-green-50 text-green-700 border-green-200',
  },
  inactive: {
    label: 'Inactive',
    icon: CircleDot,
    className: 'bg-gray-50 text-gray-600 border-gray-200',
  },
  pending: {
    label: 'Pending',
    icon: Clock,
    className: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle2,
    className: 'bg-green-50 text-green-700 border-green-200',
  },
  cancelled: {
    label: 'Cancelled',
    icon: XCircle,
    className: 'bg-red-50 text-red-700 border-red-200',
  },
  'in-progress': {
    label: 'In Progress',
    icon: PlayCircle,
    className: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  paused: {
    label: 'Paused',
    icon: PauseCircle,
    className: 'bg-purple-50 text-purple-700 border-purple-200',
  },
  overdue: {
    label: 'Overdue',
    icon: AlertCircle,
    className: 'bg-red-50 text-red-700 border-red-200',
  },
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  showIcon = true,
  className,
  children,
}) => {
  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <Badge
      variant="outline"
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-0.5 font-medium',
        config.className,
        className
      )}
    >
      {showIcon && <Icon className="h-3.5 w-3.5" />}
      {children || config.label}
    </Badge>
  );
};

export default StatusBadge;